/**
 * Configurações da mesa (system/roomSettings; TfrmRoomSettingsModule): navegação
 * vertical com Perfil (TfrmRoomProfileStateForm), Permissões
 * (TfrmRoomPermissionsStateForm) e Sistema (TfrmRoomSystemStateForm: mestres
 * auxiliares, banidos e apagar a mesa). Regras de jogo (TfrmRoomGameSettingsStateForm):
 * as barrinhas e as linhas dos cartões dos jogadores (docs/spec/game-rules.md) e o registro das
 * sessões com os relógios (docs/spec/session-clock.md).
 * Plugins (RoomPluginsPage): os plugins e as tabelas da mesa, que no original ficam nas
 * configurações gerais.
 *
 * A aparência é a das Configurações do programa (docs/spec/settings.md): as mesmas peças
 * `.rd-prefs` (a barra lateral com ícones, o título com o selo, os cards com linhas). O Perfil e as
 * Regras de jogo gravam pela barra de "alterações não salvas"; o resto vale na hora.
 */
import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { api } from "../../api.js";
import { mergeIncoming } from "../formSync.js";
import { RoomMark } from "../../explore/RoomTile.js";
import { frequencyOptions, languageOptions, visualsOptions } from "../../explore/roomDetails.js";
import { cssUrl, generatedCover, roomImageSrc, type RoomImage } from "../../explore/roomImages.js";
import { TagSelector } from "../../explore/TagSelector.js";
import { useFileUrl } from "../../files.js";
import { useLanguage, useT } from "../../i18n/index.js";
import {
  deskFileRefId,
  GAME_RULES_LIMITS,
  isGameSessionOpen,
  MEMBER_BAR_COLORS,
  normalizeGameRules,
  ROOM_ABOUT_MAX,
  ROOM_IMAGE_LIMITS,
  ROOM_PRICE_MAX,
  sessionRules,
  type BarRule,
  type GameRules,
  type Room,
  type RoomSettingsPatchInput,
  type SessionRules,
  type User,
} from "../../shared.js";
import { Avatar } from "../../social/Avatar.js";
import { useApp } from "../../store.js";
import { Button, ComboBox, Segmented, Switch, TextEdit } from "../../ui/controls.js";
import { Icon, type IconName } from "../../ui/icons.js";
import { PageHeader } from "../../ui/PageHeader.js";
import { ImagePickerDialog } from "../../ui/ImagePicker.js";
import { askText } from "../../ui/prompt.js";
import { toast } from "../../ui/Toaster.js";
import { RoomPluginsPage } from "./RoomPluginsPage.js";
import { useRoomClock } from "../clock/ClockPill.js";
// as peças das Configurações do programa
import "../../config/settings.css";

type Tab = "profile" | "game" | "plugins" | "permissions" | "system";

const ALL_TABS: readonly Tab[] = ["profile", "game", "plugins", "permissions", "system"];
// quem não administra a mesa só vê os plugins (a lista, as tabelas e o modo de desenvolvimento)
const PLAYER_TABS: readonly Tab[] = ["plugins"];

/** O rodapé do painel, fora da área que rola: a barra de alterações não salvas vai para ele (BRAZ-34). */
const SaveBarSlot = createContext<HTMLElement | null>(null);
const TAB_ICONS: Record<Tab, IconName> = {
  profile: "edit",
  game: "die",
  plugins: "grid",
  permissions: "lock",
  system: "shield",
};

export function RoomSettingsModule() {
  const t = useT();
  const room = useApp((s) => s.room);
  const canAdminister = useApp((s) => !!s.room && !!s.user && (s.room.ownerId === s.user.id || !!s.members[s.user.id]?.modes.includes("gm")));
  const tabs = canAdminister ? ALL_TABS : PLAYER_TABS;
  const [chosen, setTab] = useState<Tab>(canAdminister ? "profile" : "plugins");
  // perdeu o papel de mestre com a aba aberta: vai para uma que ainda vale
  const tab = tabs.includes(chosen) ? chosen : tabs[0]!;
  const uid = useId();
  const main = useRef<HTMLDivElement>(null);
  const [footer, setFooter] = useState<HTMLDivElement | null>(null);
  // cada página começa do topo
  useEffect(() => {
    main.current?.scrollTo?.({ top: 0 });
  }, [tab]);
  if (!room) return null;
  const tabId = (id: Tab) => `${uid}-tab-${id}`;
  return (
    <div className="rd-settings-host">
      <div className={`rd-prefs rd-prefs--room rd-settings${tabs.length === 1 ? " rd-prefs--single" : ""}`}>
        <aside className="rd-prefs__side">
          <div className="rd-prefs__room">
            <RoomMark card={room} className="rd-prefs__roomMark" />
            <span className="rd-prefs__meText">
              <strong>{room.name}</strong>
              <span>{t("room.settings")}</span>
            </span>
          </div>
          <nav className="rd-prefs__nav" aria-label={t("settings.nav")}>
            <div role="tablist" aria-orientation="vertical" className="rd-prefs__tabs">
              {tabs.map((id) => (
                <button
                  key={id}
                  id={tabId(id)}
                  type="button"
                  role="tab"
                  aria-selected={tab === id}
                  aria-controls={`${uid}-page`}
                  className={`rd-navItem rd-prefs__navItem${tab === id ? " is-on" : ""}`}
                  onClick={() => setTab(id)}
                >
                  <Icon name={TAB_ICONS[id]} size={18} />
                  <span>{t(`settings.tab.${id}`)}</span>
                </button>
              ))}
            </div>
          </nav>
        </aside>
        <div className="rd-prefs__body">
          <div className="rd-prefs__main" ref={main}>
            <div className="rd-prefs__page">
              <section id={`${uid}-page`} role="tabpanel" aria-labelledby={tabId(tab)} className="rd-prefs__section">
                <PageHeader
                  className="rd-prefs__head"
                  level={2}
                  title={t(`settings.tab.${tab}`)}
                  subtitle={t(`settings.sub.${tab}`)}
                  actions={
                    <span className="rd-prefs__scope" title={t("settings.scope.tip")}>
                      <Icon name="rooms" size={14} />
                      {t("settings.scope")}
                    </span>
                  }
                />
                <SaveBarSlot.Provider value={footer}>
                  {tab === "profile" ? <ProfilePage room={room} /> : null}
                  {tab === "game" ? <GamePage room={room} /> : null}
                  {tab === "plugins" ? <RoomPluginsPage room={room} /> : null}
                  {tab === "permissions" ? <PermissionsPage room={room} /> : null}
                  {tab === "system" ? <SystemPage room={room} /> : null}
                </SaveBarSlot.Provider>
              </section>
            </div>
          </div>
          <div className="rd-prefs__footer" ref={setFooter} />
        </div>
      </div>
    </div>
  );
}

function useSave(roomId: string) {
  const t = useT();
  const token = useApp((s) => s.token);
  const [busy, setBusy] = useState(false);
  const save = useCallback(
    async (patch: RoomSettingsPatchInput): Promise<boolean> => {
      if (!token) return false;
      setBusy(true);
      try {
        await api.updateRoomSettings(token, roomId, patch);
        toast(t("settings.saved"), "success");
        return true;
      } catch (error) {
        toast(error instanceof Error ? error.message : String(error), "error");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [token, roomId, t],
  );
  return { save, busy };
}

/** Um card das configurações: o título, a descrição e as linhas. */
function Card({
  title,
  desc,
  danger = false,
  aside,
  children,
}: {
  title: string;
  desc?: string;
  danger?: boolean;
  aside?: ReactNode;
  children?: ReactNode;
}) {
  const id = useId();
  return (
    <section className={`rd-prefs__card${danger ? " rd-prefs__card--danger" : ""}`} aria-labelledby={id}>
      <header className={`rd-prefs__cardHead${aside ? " rd-prefs__cardHead--row" : ""}`}>
        <div>
          <h3 id={id}>{title}</h3>
          {desc ? <p className="rd-prefs__desc">{desc}</p> : null}
        </div>
        {aside}
      </header>
      {children}
    </section>
  );
}

/**
 * Uma linha: o rótulo e a descrição à esquerda, o controle à direita. `id` é o do controle; o
 * rótulo fica em `${id}-label`, para os controles que se nomeiam por `aria-labelledby`.
 */
function Row({ id, label, desc, children }: { id: string; label: string; desc?: ReactNode; children: ReactNode }) {
  return (
    <div className="rd-prefs__row">
      <div className="rd-prefs__rowText">
        <label htmlFor={id} id={`${id}-label`} className="rd-prefs__label">
          {label}
        </label>
        {desc ? (
          <p className="rd-prefs__desc" id={`${id}-desc`}>
            {desc}
          </p>
        ) : null}
      </div>
      <div className="rd-prefs__control">{children}</div>
    </div>
  );
}

function SwitchRow({ label, desc, checked, disabled, onChange }: { label: string; desc: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
  const id = useId();
  return (
    <Row id={id} label={label} desc={desc}>
      <Switch id={id} checked={checked} disabled={disabled} onChange={onChange} aria-describedby={`${id}-desc`} />
    </Row>
  );
}

/**
 * A barra de alterações não salvas (como no Perfil do programa). Fica no rodapé do painel, fora da área
 * que rola, e por isso nunca cobre um campo (BRAZ-34): quando ela aparece, a área encolhe, e o campo
 * em edição volta à vista se ficou embaixo. O Salvar envia o formulário `form` de longe.
 */
function SaveBar({ form, busy, canSave, onDiscard }: { form: string; busy: boolean; canSave: boolean; onDiscard: () => void }) {
  const t = useT();
  const slot = useContext(SaveBarSlot);
  useLayoutEffect(() => {
    const field = document.activeElement;
    if (slot && field instanceof HTMLElement && field.closest(".rd-prefs__main")) field.scrollIntoView?.({ block: "nearest" });
  }, [slot]);
  const bar = (
    <div className="rd-prefs__unsaved" role="region" aria-label={t("settings.unsaved")}>
      <span>{t("settings.unsaved")}</span>
      <Button type="button" variant="secondary" onClick={onDiscard}>
        {t("settings.discard")}
      </Button>
      <Button type="submit" form={form} variant="action" busy={busy} disabled={!canSave}>
        {t("settings.save")}
      </Button>
    </div>
  );
  return slot ? createPortal(bar, slot) : bar;
}

function ProfilePage({ room }: { room: Room }) {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const id = useId();
  const { save, busy } = useSave(room.id);
  const initial = () => ({
    name: room.name,
    rpgSystem: room.rpgSystem,
    rpgSystemComplement: room.rpgSystemComplement,
    tags: room.tags,
    welcomeMessage: room.welcomeMessage,
    statusMessage: room.statusMessage,
    coverUrl: room.coverUrl ?? "",
    logoUrl: room.logoUrl ?? "",
    haveVacancies: room.haveVacancies,
    restrictedUnder18: room.restrictedUnder18,
    isPrivate: room.isPrivate,
    language: room.language,
    frequency: room.frequency,
    visuals: room.visuals,
    payToPlay: room.payToPlay,
    price: room.price,
    about: room.about,
  });
  const [form, setForm] = useState(initial);
  // quando outra pessoa salva, o formulário acompanha (room.updated), sem perder o que se está editando
  const base = useRef(initial());
  useEffect(() => {
    const next = initial();
    const before = base.current;
    base.current = next;
    setForm((current) => mergeIncoming(current, before, next));
  }, [room]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = <K extends keyof ReturnType<typeof initial>>(
    key: K,
    value: ReturnType<typeof initial>[K],
  ) => setForm((f) => ({ ...f, [key]: value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(initial());
  // a capa ou a marca sendo escolhida: o seletor fica fora do <form>, porque o submit sintético do
  // React subiria até ele (o Enter no endereço da web salvaria a mesa)
  const [picking, setPicking] = useState<RoomImage | null>(null);
  const field = (which: RoomImage) => (which === "cover" ? "coverUrl" : "logoUrl");

  return (
    <>
      <form
        id={`${id}-form`}
        className="rd-prefs__section"
        onSubmit={(e) => {
          e.preventDefault();
          void save({
            ...form,
            coverUrl: form.coverUrl || null,
            logoUrl: form.logoUrl || null,
          });
        }}
      >
        <Card title={t("settings.card.presentation")} desc={t("settings.card.presentation.desc")}>
          <div className="rd-prefs__cardBody">
            <Field label={t("settings.profile.name")}>
              <TextEdit
                look="filled"
                value={form.name}
                onChange={(v) => set("name", v)}
                maxLength={60}
              />
            </Field>
            <div className="rd-prefs__pair">
              <Field label={t("create.system")}>
                <TextEdit
                  look="filled"
                  value={form.rpgSystem}
                  onChange={(v) => set("rpgSystem", v)}
                  maxLength={60}
                  placeholder={t("create.system.prompt")}
                />
              </Field>
              <Field label={t("create.complement")}>
                <TextEdit
                  look="filled"
                  value={form.rpgSystemComplement}
                  onChange={(v) => set("rpgSystemComplement", v)}
                  maxLength={60}
                  placeholder={t("create.complement.prompt")}
                />
              </Field>
            </div>
            <Field label={t("settings.profile.status")}>
              <TextEdit
                look="filled"
                value={form.statusMessage}
                onChange={(v) => set("statusMessage", v)}
                maxLength={120}
                placeholder={t("settings.profile.status.prompt")}
              />
            </Field>
            <Field label={t("create.welcome")}>
              <textarea
                className="rd-edit rd-edit--filled rd-create__welcome"
                rows={4}
                maxLength={500}
                value={form.welcomeMessage}
                onChange={(e) => set("welcomeMessage", e.target.value)}
              />
            </Field>
          </div>
        </Card>
        <Card title={t("settings.card.details")} desc={t("settings.card.details.desc")}>
          <Row id={`${id}-language`} label={t("roomDetails.language")}>
            <ComboBox id={`${id}-language`} value={form.language} onChange={(v) => set("language", v)} options={languageOptions(t, language)} />
          </Row>
          <Row id={`${id}-frequency`} label={t("roomDetails.frequency")}>
            <ComboBox id={`${id}-frequency`} value={form.frequency} onChange={(v) => set("frequency", v)} options={frequencyOptions(t)} />
          </Row>
          <Row id={`${id}-visuals`} label={t("roomDetails.visuals")}>
            <ComboBox id={`${id}-visuals`} value={form.visuals} onChange={(v) => set("visuals", v)} options={visualsOptions(t)} />
          </Row>
          <SwitchRow
            label={t("roomDetails.payToPlay")}
            desc={t("roomDetails.payToPlay.desc")}
            checked={form.payToPlay}
            onChange={(v) => set("payToPlay", v)}
          />
          {form.payToPlay ? (
            <div className="rd-prefs__cardBody">
              <Field label={t("roomDetails.price")}>
                <TextEdit
                  look="filled"
                  value={form.price}
                  onChange={(v) => set("price", v)}
                  maxLength={ROOM_PRICE_MAX}
                  placeholder={t("roomDetails.price.prompt")}
                />
              </Field>
            </div>
          ) : null}
        </Card>
        <Card title={t("roomDetails.about")} desc={t("settings.card.about.desc")}>
          <div className="rd-prefs__cardBody">
            <textarea
              className="rd-edit rd-edit--filled rd-create__welcome rd-prefs__about"
              maxLength={ROOM_ABOUT_MAX}
              value={form.about}
              onChange={(e) => set("about", e.target.value)}
              placeholder={t("roomDetails.about.prompt")}
              aria-label={t("roomDetails.about")}
            />
          </div>
        </Card>
        <Card title={t("create.tags")} desc={t("settings.card.tags.desc")}>
          <div className="rd-prefs__cardBody">
            <TagSelector
              value={form.tags}
              onChange={(v) => set("tags", v)}
              max={12}
            />
          </div>
        </Card>
        <Card title={t("settings.card.images")} desc={t("settings.card.images.desc")}>
          <div className="rd-prefs__cardBody">
            <div className="rd-prefs__pair">
              {(["cover", "logo"] as const).map((which) => (
                <ImageSlot
                  key={which}
                  room={room}
                  which={which}
                  value={form[field(which)]}
                  name={form.name}
                  onChoose={() => setPicking(which)}
                  onRemove={() => set(field(which), "")}
                />
              ))}
            </div>
          </div>
        </Card>
        <Card title={t("settings.card.explore")}>
          <SwitchRow
            label={t("settings.profile.vacancies")}
            desc={t("settings.profile.vacancies.desc")}
            checked={form.haveVacancies}
            onChange={(v) => set("haveVacancies", v)}
          />
          <SwitchRow
            label={t("settings.profile.adult")}
            desc={t("settings.profile.adult.desc")}
            checked={form.restrictedUnder18}
            onChange={(v) => set("restrictedUnder18", v)}
          />
          <SwitchRow
            label={t("settings.profile.private")}
            desc={t("settings.profile.private.desc")}
            checked={form.isPrivate}
            onChange={(v) => set("isPrivate", v)}
          />
        </Card>
        {dirty ? (
          <SaveBar
            form={`${id}-form`}
            busy={busy}
            canSave={form.name.trim().length >= 5}
            onDiscard={() => setForm(initial())}
          />
        ) : null}
      </form>
      {picking ? (
        <ImagePickerDialog
          cloud
          rules={{
            types: ROOM_IMAGE_LIMITS.types,
            maxBytes: ROOM_IMAGE_LIMITS.maxBytes,
            maxSide: picking === "cover" ? ROOM_IMAGE_LIMITS.coverSide : ROOM_IMAGE_LIMITS.logoSide,
          }}
          title={t(picking === "cover" ? "settings.profile.cover" : "settings.profile.logo")}
          initial={form[field(picking)]}
          onPick={(value) => {
            set(field(picking), value);
            setPicking(null);
          }}
          onCancel={() => setPicking(null)}
        />
      ) : null}
    </>
  );
}

/**
 * A capa ou a marca da mesa: a prévia, "Escolher imagem…" e "Remover". A imagem escolhida agora, ainda
 * não salva, aparece pelo link do arquivo de quem a escolheu. A salva aparece pela rota pública da
 * mesa, que abre também para os outros mestres. A que não abre (o arquivo foi para a lixeira, o
 * endereço sumiu) mostra a arte gerada e um aviso.
 */
function ImageSlot({
  room,
  which,
  value,
  name,
  onChoose,
  onRemove,
}: {
  room: Room;
  which: RoomImage;
  value: string;
  name: string;
  onChoose: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const labelId = useId();
  const saved = (which === "cover" ? room.coverUrl : room.logoUrl) ?? "";
  const pickedId = value !== saved ? deskFileRefId(value) : null;
  const picked = useFileUrl(pickedId);
  const src = !value
    ? null
    : pickedId
      ? typeof picked === "string" && picked !== "error"
        ? picked
        : null
      : roomImageSrc(room.id, which, value);
  const [broken, setBroken] = useState<string | null>(null);
  const failed = picked === "error" || (!!src && broken === src);
  const shown = failed ? null : src;
  return (
    <div className="rd-prefs__image" role="group" aria-labelledby={labelId}>
      <span className="rd-prefs__fieldLabel" id={labelId}>
        {t(which === "cover" ? "settings.profile.cover" : "settings.profile.logo")}
      </span>
      {which === "cover" ? (
        <span
          className="rd-prefs__imageCover"
          style={{ backgroundImage: shown ? `${cssUrl(shown)}, ${generatedCover(room.id)}` : generatedCover(room.id) }}
          aria-hidden="true"
        />
      ) : (
        <RoomMark card={{ id: room.id, name, logoUrl: value || null }} className="rd-prefs__imageBadge" src={shown} />
      )}
      {src ? <img src={src} alt="" hidden onError={() => setBroken(src)} /> : null}
      <span className={`rd-prefs__imageHint${failed ? " is-broken" : ""}`}>
        {t(failed ? "settings.profile.image.broken" : which === "cover" ? "settings.profile.cover.hint" : "settings.profile.logo.hint")}
      </span>
      <span className="rd-prefs__imageActions">
        <Button variant="secondary" onClick={onChoose}>
          {t("settings.profile.image.pick")}
        </Button>
        {value ? (
          <Button variant="secondary" onClick={onRemove}>
            {t("settings.profile.image.remove")}
          </Button>
        ) : null}
      </span>
    </div>
  );
}

/** Regras de jogo: quantas barrinhas e linhas os cartões mostram, e como; se os dados 3D animam e de onde vêm os números deles. */
function GamePage({ room }: { room: Room }) {
  const t = useT();
  const { save, busy } = useSave(room.id);
  const id = useId();
  // a escolha dos números dos dados só aparece quando o servidor tem a chave do random.org
  const randomOrg = useApp((s) => s.serverFeatures.randomOrg);
  const initial = () => normalizeGameRules(room.gameRules);
  const [form, setForm] = useState<GameRules>(initial);
  const base = useRef(initial());
  useEffect(() => {
    const next = initial();
    const before = base.current;
    base.current = next;
    setForm((current) => mergeIncoming(current, before, next));
  }, [room.gameRules]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(form) !== JSON.stringify(initial());
  const setBar = (i: number, patch: Partial<BarRule>) =>
    setForm((f) => ({ ...f, bars: f.bars.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const setLine = (i: number, name: string) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, j) => (j === i ? { name } : l)) }));
  const setSessions = (patch: Partial<SessionRules>) => setForm((f) => ({ ...f, sessions: { ...f.sessions, ...patch } }));
  // desligar o registro encerra a sessão aberta ao salvar (docs/spec/session-clock.md, "Ligar e desligar")
  const live = useRoomClock()?.session ?? null;
  const ending = isGameSessionOpen(live) && sessionRules(room).record && !form.sessions.record ? live.number : null;
  const counts = (max: number) =>
    Array.from({ length: max + 1 }, (_, n) => ({
      value: String(n),
      label: n === 0 ? t("settings.game.none") : String(n),
    }));

  return (
    <form
      id={`${id}-form`}
      className="rd-prefs__section"
      onSubmit={(e) => {
        e.preventDefault();
        void save({ gameRules: form });
      }}
    >
      <Card title={t("settings.game.dice")}>
        <SwitchRow
          label={t("settings.game.dice.animation")}
          desc={t("settings.game.dice.animation.hint")}
          checked={form.diceAnimation}
          onChange={(diceAnimation) => setForm((f) => ({ ...f, diceAnimation }))}
        />
        {randomOrg ? (
          <Row id={`${id}-dice`} label={t("settings.game.dice.source")} desc={t("settings.game.dice.hint")}>
            <ComboBox
              id={`${id}-dice`}
              value={form.diceSource}
              onChange={(v) => setForm((f) => ({ ...f, diceSource: v }))}
              options={[
                { value: "program", label: t("settings.game.dice.program") },
                { value: "random.org", label: t("settings.game.dice.randomOrg") },
              ]}
            />
          </Row>
        ) : null}
      </Card>
      <Card title={t("settings.game.bars")} desc={t("settings.game.bars.hint")}>
        <Row id={`${id}-bars`} label={t("settings.game.bars.count")}>
          <ComboBox
            id={`${id}-bars`}
            value={String(form.barCount)}
            onChange={(v) => setForm((f) => ({ ...f, barCount: Number(v) }))}
            options={counts(GAME_RULES_LIMITS.bars)}
          />
        </Row>
        {form.barCount ? (
          <div className="rd-prefs__blockRow">
            <ul className="rd-rules">
              {form.bars.slice(0, form.barCount).map((bar, i) => {
                const n = i + 1;
                return (
                  <li key={i} className="rd-rules__bar">
                    <span className="rd-rules__color">
                      <input
                        type="color"
                        value={bar.color ?? MEMBER_BAR_COLORS[i]}
                        aria-label={t("settings.game.bar.color", { n })}
                        onChange={(e) => setBar(i, { color: e.target.value })}
                      />
                      {bar.color ? (
                        <button
                          type="button"
                          className="rd-rules__reset"
                          title={t("settings.game.bar.color.reset")}
                          aria-label={t("settings.game.bar.color.reset")}
                          onClick={() => setBar(i, { color: null })}
                        >
                          <Icon name="close" size={12} />
                        </button>
                      ) : null}
                    </span>
                    <TextEdit
                      look="filled"
                      value={bar.name}
                      onChange={(v) => setBar(i, { name: v })}
                      maxLength={GAME_RULES_LIMITS.name}
                      placeholder={t("users.card.bar", { n })}
                      aria-label={t("settings.game.bar.name", { n })}
                    />
                    <ComboBox
                      value={bar.numbers}
                      onChange={(v) => setBar(i, { numbers: v })}
                      aria-label={t("settings.game.bar.numbers", { n })}
                      options={[
                        { value: "everyone", label: t("settings.game.numbers.everyone") },
                        { value: "gmOwner", label: t("settings.game.numbers.gmOwner") },
                      ]}
                    />
                    <ComboBox
                      value={bar.kind}
                      onChange={(v) => setBar(i, { kind: v })}
                      aria-label={t("settings.game.bar.kind", { n })}
                      options={[
                        { value: "values", label: t("settings.game.kind.values") },
                        { value: "percent", label: t("settings.game.kind.percent") },
                      ]}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </Card>
      <Card title={t("settings.game.lines")} desc={t("settings.game.lines.hint")}>
        <Row id={`${id}-lines`} label={t("settings.game.lines.count")}>
          <ComboBox
            id={`${id}-lines`}
            value={String(form.lineCount)}
            onChange={(v) => setForm((f) => ({ ...f, lineCount: Number(v) }))}
            options={counts(GAME_RULES_LIMITS.lines)}
          />
        </Row>
        {form.lineCount ? (
          <div className="rd-prefs__blockRow">
            <ul className="rd-rules">
              {form.lines.slice(0, form.lineCount).map((line, i) => (
                <li key={i} className="rd-rules__line">
                  <TextEdit
                    look="filled"
                    value={line.name}
                    onChange={(v) => setLine(i, v)}
                    maxLength={GAME_RULES_LIMITS.name}
                    placeholder={t("users.card.line", { n: i + 1 })}
                    aria-label={t("settings.game.line.name", { n: i + 1 })}
                  />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Card>
      <Card title={t("settings.game.sessions")} desc={t("settings.game.sessions.hint")}>
        <SwitchRow
          label={t("settings.game.sessions.record")}
          desc={t("settings.game.sessions.record.hint")}
          checked={form.sessions.record}
          onChange={(record) => setSessions({ record })}
        />
        {ending ? (
          <p className="rd-prefs__row rd-prefs__desc rd-prefs__ending" role="status">
            {t("settings.game.sessions.ending", { n: ending })}
          </p>
        ) : null}
        <SwitchRow
          label={t("settings.game.sessions.chatClock")}
          desc={t("settings.game.sessions.chatClock.hint")}
          checked={form.sessions.chatClock}
          disabled={!form.sessions.record}
          onChange={(chatClock) => setSessions({ chatClock })}
        />
        <SwitchRow
          label={t("settings.game.sessions.barClock")}
          desc={t("settings.game.sessions.barClock.hint")}
          checked={form.sessions.barClock}
          disabled={!form.sessions.record}
          onChange={(barClock) => setSessions({ barClock })}
        />
      </Card>
      {dirty ? <SaveBar form={`${id}-form`} busy={busy} canSave onDiscard={() => setForm(initial())} /> : null}
    </form>
  );
}

function PermissionsPage({ room }: { room: Room }) {
  const t = useT();
  const { save, busy } = useSave(room.id);
  const [password, setPassword] = useState("");
  const id = useId();
  return (
    <>
      <Card title={t("settings.card.password")}>
        <Row
          id={`${id}-password`}
          label={t("settings.permissions.password")}
          desc={room.hasPassword ? t("settings.permissions.password.has") : t("settings.permissions.password.none")}
        >
          <TextEdit
            id={`${id}-password`}
            look="filled"
            icon="lock"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder={t("settings.permissions.password.new")}
            maxLength={120}
          />
          <Button
            variant="action"
            busy={busy}
            disabled={!password}
            onClick={() =>
              void save({ password }).then((ok) => ok && setPassword(""))
            }
          >
            {t("settings.permissions.password.set")}
          </Button>
          {room.hasPassword ? (
            <Button
              variant="secondary"
              onClick={() => void save({ password: null })}
            >
              {t("settings.permissions.password.remove")}
            </Button>
          ) : null}
        </Row>
      </Card>
      <Card title={t("settings.card.chat")}>
        <Row
          id={`${id}-log`}
          label={t("settings.permissions.log")}
          desc={t(`settings.permissions.log.${room.logAccess}`)}
        >
          <Segmented
            aria-labelledby={`${id}-log-label`}
            aria-describedby={`${id}-log-desc`}
            value={room.logAccess}
            onChange={(mode) => void save({ logAccess: mode })}
            options={[
              { value: "everyone", label: t("settings.permissions.log.everyone.short") },
              { value: "receipt", label: t("settings.permissions.log.receipt.short") },
            ]}
          />
        </Row>
      </Card>
    </>
  );
}

function SystemPage({ room }: { room: Room }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const me = useApp((s) => s.user);
  const deleteRoom = useApp((s) => s.deleteRoom);
  const [people, setPeople] = useState<{ auxGms: User[]; bans: User[] } | null>(
    null,
  );
  const isOwner = room.ownerId === me?.id;
  const id = useId();

  const reload = useCallback(async () => {
    if (!token) return;
    try {
      setPeople(await api.roomPeople(token, room.id));
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), "error");
    }
  }, [token, room.id]);
  useEffect(() => void reload(), [reload]);

  async function change(action: () => Promise<unknown>) {
    try {
      await action();
      await reload();
    } catch (error) {
      toast(error instanceof Error ? error.message : String(error), "error");
    }
  }

  if (!people)
    return <p className="rd-prefs__desc">{t("explore.loading")}</p>;
  return (
    <>
      <PeopleList
        title={t("settings.system.auxGms")}
        hint={t("settings.system.auxGms.hint")}
        users={people.auxGms}
        editable={isOwner}
        onAdd={(account) =>
          change(() => api.addRoomPerson(token!, room.id, "aux-gms", account))
        }
        onRemove={(id) =>
          change(() => api.removeRoomPerson(token!, room.id, "aux-gms", id))
        }
      />
      <PeopleList
        title={t("settings.system.bans")}
        hint={t("settings.system.bans.hint")}
        users={people.bans}
        editable
        onAdd={(account) =>
          change(() => api.addRoomPerson(token!, room.id, "bans", account))
        }
        onRemove={(id) =>
          change(() => api.removeRoomPerson(token!, room.id, "bans", id))
        }
      />
      {isOwner ? (
        <Card title={t("prefs.account.danger")} danger>
          <Row id={`${id}-delete`} label={t("settings.system.delete")} desc={t("settings.system.delete.hint")}>
            <Button
              id={`${id}-delete`}
              variant="secondary"
              className="rd-prefs__dangerBtn"
              onClick={async () => {
                const typed = await askText({ title: t("settings.system.delete"), label: t("settings.system.delete.confirm", { name: room.name }), maxLength: 80, action: t("settings.system.delete") });
                if (typed === room.name.trim()) void deleteRoom(room.id);
                else if (typed !== null) toast(t("settings.system.delete.mismatch"), "error");
              }}
            >
              {t("settings.system.delete")}
            </Button>
          </Row>
        </Card>
      ) : null}
    </>
  );
}

function PeopleList({
  title,
  hint,
  users,
  editable,
  onAdd,
  onRemove,
}: {
  title: string;
  hint: string;
  users: User[];
  editable: boolean;
  onAdd: (account: string) => Promise<void>;
  onRemove: (userId: string) => Promise<void>;
}) {
  const t = useT();
  const [account, setAccount] = useState("");
  return (
    <Card title={title} desc={hint}>
      {editable ? (
        <form
          className="rd-prefs__blockRow rd-prefs__add"
          onSubmit={(e) => {
            e.preventDefault();
            if (account.trim())
              void onAdd(account.trim()).then(() => setAccount(""));
          }}
        >
          <TextEdit
            look="filled"
            icon="user"
            value={account}
            onChange={setAccount}
            placeholder={t("settings.system.account")}
            aria-label={`${title}: ${t("settings.system.account")}`}
          />
          <Button type="submit" variant="action" disabled={!account.trim()}>
            {t("settings.system.add")}
          </Button>
        </form>
      ) : null}
      {users.length === 0 ? (
        <p className="rd-prefs__row rd-prefs__desc">{t("settings.system.empty")}</p>
      ) : (
        <ul className="rd-prefs__people">
          {users.map((u) => (
            <li key={u.id} className="rd-prefs__row">
              <span className="rd-prefs__person">
                <Avatar name={u.displayName} color={u.color} size={32} userId={u.id} avatarVersion={u.avatarVersion} />
                {u.displayName}
              </span>
              {editable ? (
                <Button
                  variant="link"
                  className="rd-prefs__link"
                  aria-label={`${t("settings.system.remove")}: ${u.displayName}`}
                  onClick={() => void onRemove(u.id)}
                >
                  {t("settings.system.remove")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="rd-prefs__field">
      <span className="rd-prefs__fieldLabel">{label}</span>
      {children}
    </label>
  );
}
